import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type CleanerAppLanguage = "en" | "es";

export function normalizeCleanerAppLanguage(value: string | null | undefined): CleanerAppLanguage {
  return value === "es" ? "es" : "en";
}

export function useCleanerAppLanguages(userIds: string[]) {
  const key = [...userIds].filter(Boolean).sort().join(",");
  return useQuery({
    queryKey: ["cleaner-app-languages", key],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("list_cleaner_app_languages", {
        p_user_ids: userIds,
      });
      if (error) throw error;
      const rows = (data ?? []) as { user_id: string; language: string }[];
      const map: Record<string, CleanerAppLanguage> = {};
      for (const row of rows) {
        map[row.user_id] = normalizeCleanerAppLanguage(row.language);
      }
      return map;
    },
  });
}

export function useSetCleanerAppLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, language }: { userId: string; language: CleanerAppLanguage }) => {
      const { data, error } = await supabase.rpc("set_cleaner_app_language", {
        p_target_user_id: userId,
        p_language: language,
      });
      if (error) throw error;
      return normalizeCleanerAppLanguage(typeof data === "string" ? data : language);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cleaner-app-languages"] });
    },
  });
}
